import { computed, ref } from "vue";
import { defineStore } from "pinia";
import useMyFetch from "./useMyFetch";
import { userType, epistoUserTypes } from "@utils/constants/userRoles";
import { captureEvent } from "@/lib/analytics";

export const useUsersStore = defineStore("users", () => {
  // state
  const users = ref([]);
  const usersCount = ref(0);
  const currentPage = ref(1);

  // actions
  async function fetch({ page = 1, search = "", userTypes = null, roles = null, account = null, per_page = 10 } = {}) {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("per_page", per_page);
    if (search) params.append("search", search);
    appendAll(params, "user_type[]", userTypes);
    appendAll(params, "role_ids[]", roles);
    appendAll(params, "account_ids[]", account);

    const { data, response } = await useMyFetch(`/users?${params.toString()}`).json();

    if (response.value.ok) {
      users.value = data.value.users;
      usersCount.value = data.value.users_count;
      currentPage.value = page;
    }
  }

  function appendAll(params, key, value) {
    if (!value || value === "all") return;
    const values = Array.isArray(value) ? value : [value];
    values.forEach((entry) => params.append(key, entry));
  }

  async function createUser(payload) {
    const { data, error } = await useMyFetch("/users").post({ user: payload }).json();
    if (error.value) throw error.value;

    captureEvent("user_created", { user_id: data.value?.id, user_type: payload.user_type });
    users.value.push(data.value);
    return data.value;
  }

  async function updateUser(id, payload) {
    const { data, error } = await useMyFetch(`/users/${id}`).put({ user: payload }).json();
    if (error.value) throw error.value;

    captureEvent("user_updated", { user_id: id });
    const index = users.value.findIndex((u) => u.id === id);
    if (index !== -1) users.value[index] = data.value;
    return data.value;
  }

  async function deleteUser(id) {
    const { error } = await useMyFetch(`/users/${id}`).delete();
    if (error.value) throw error.value;

    captureEvent("user_deleted", { user_id: id });

    users.value = users.value.filter((u) => u.id !== id);
  }

  async function resetPassword(id) {
    const { error } = await useMyFetch(`/users/${id}/password_reset`).post();
    if (error.value) throw error.value;

    captureEvent("user_password_reset_sent", { user_id: id });
  }

  // Non-mutating lookup: resolves a single client user by exact email, without
  // touching the paginated `users` state. Returns { found, ...user } or { found: false }.
  async function lookupByEmail(email) {
    const params = new URLSearchParams({ email });
    const { data, error } = await useMyFetch(`/users/lookup?${params.toString()}`).json();
    if (error.value) throw error.value;
    return data.value;
  }

  // Non-mutating fuzzy search (first/last/email) used by typeahead pickers,
  // without touching the paginated `users` state.
  async function searchUsers(query) {
    const params = new URLSearchParams({ search: query, per_page: "8", page: "1" });
    const { data, error } = await useMyFetch(`/users?${params.toString()}`).json();
    if (error.value) throw error.value;
    return data.value?.users ?? [];
  }

  // Non-mutating: returns the client users attached to an account, without
  // touching the paginated `users` state.
  async function clientUsersForAccount(accountId) {
    const params = new URLSearchParams({ per_page: "100" });
    params.append("account_ids[]", accountId);
    params.append("user_type[]", userType.CLIENT);
    const { data, error } = await useMyFetch(`/users?${params.toString()}`).json();
    if (error.value) throw error.value;
    return data.value?.users ?? [];
  }

  const adminSalesUsers = computed(() => users.value.filter((user) => epistoUserTypes.includes(user.user_type)));
  const adminSalesOptions = computed(() =>
    adminSalesUsers.value.map((user) => ({ label: `${user.first_name} ${user.last_name}`, value: user.id })),
  );

  return {
    // state
    users,
    usersCount,
    currentPage,
    // computed
    adminSalesUsers,
    adminSalesOptions,
    // actions
    fetch,
    createUser,
    updateUser,
    deleteUser,
    resetPassword,
    lookupByEmail,
    searchUsers,
    clientUsersForAccount,
  };
});
